<template>
    <main>
        <ProfileViewsUpgradeDialog />
        <ProfileViewsAbuseDialog />
        <TooMuchSearch v-if="performedTooMuchSearch" />
        <StrategyHighLevelWrapper v-else>
            <SideStickyLayout
                open-sidebar-button-icon="filter"
                :open-sidebar-button-label="$t('search.filters.filter')"
                close-sidebar-button-icon="check"
                :close-sidebar-button-label="$t('search.filters.apply')"
            >
                <template #sidebar>
                    <ProfileViewsStatus />
                    <SearchFilters />
                </template>
                <template #main>
                    <ClientOnly v-if="!isStrategyEligible">
                        <Shortlist />
                    </ClientOnly>
                    <SearchResults />
                    <ProfileCardAdminPanel />
                </template>
            </SideStickyLayout>
        </StrategyHighLevelWrapper>

        <!-- modals content for pool of freelancer -->
        <ClientOnly>
            <FavoriteModal />
            <CreateFreelancerListModal />
        </ClientOnly>

        <SearchResultsAdminPanel />
    </main>
</template>

<script setup lang="ts">
    import {getCurrentInstance, onBeforeUnmount, onMounted, watch} from 'vue';
    import {navbarEventBus, useNavbarDisplayModeStore, type NavbarSearchRequestedPayload} from '@navbar/public-api';
    import {storeToRefs} from 'pinia';
    import {useRoute, useRouter} from 'vue-router';
    import {ClientOnly} from '#components';
    import {
        definePageMeta,
        useAsyncData,
        useOpenGraphMetaTags,
        useServerSeoMeta,
        useTranslation,
        useTwitterMetaTags,
        useAmplitudeSessionReplay,
        OpenGraphMetaTag,
        TwitterMetaTags,
        useAuth,
    } from '#imports';
    import {safelyNavigateTo} from '@malt/utils';

    // Keyword search COMPONENTS
    import ProfileCardAdminPanel from '~/features/keyword-search/admin/ProfileCardAdminPanel.vue';
    import SearchResultsAdminPanel from '~/features/keyword-search/admin/SearchResultsAdminPanel.vue';
    import SideStickyLayout from '~/features/keyword-search/layouts/SideStickyLayout.vue';
    import missingQueryParamRedirect from '~/features/keyword-search/middleware/missingQueryParamRedirect';
    import CreateFreelancerListModal from '~/features/keyword-search/pool-of-freelances/create-freelancer-list/CreateFreelancerListModal.vue';
    import FavoriteModal from '~/features/keyword-search/pool-of-freelances/favorite-modal/FavoriteModal.vue';
    import ProfileViewsAbuseDialog from '~/features/keyword-search/profileviews/ProfileViewsAbuseDialog.client.vue';
    import ProfileViewsStatus from '~/features/keyword-search/profileviews/ProfileViewsStatus.vue';
    import ProfileViewsUpgradeDialog from '~/features/keyword-search/profileviews/ProfileViewsUpgradeDialog.client.vue';
    import StrategyHighLevelWrapper from '~/features/keyword-search/search-result/malt-strategy-banners/StrategyHighLevelWrapper.vue';
    import SearchResults from '~/features/keyword-search/search-result/SearchResults.vue';
    import Shortlist from '~/features/keyword-search/search-result/Shortlist.vue';
    import SearchFilters from '~/features/keyword-search/SearchFilters.vue';
    import {useFiltersStore} from '~/features/keyword-search/stores/filters.store';
    import {useProfilesStore} from '~/features/keyword-search/stores/profiles.store';
    import {useRestrictionsStore} from '~/features/keyword-search/stores/restrictions.store';
    import {useSearchFeaturesStore} from '~/features/keyword-search/stores/search-features.store';
    import {useSearchUrlStore} from '~/features/keyword-search/stores/search-url.store';
    import {useShortlistStore} from '~/features/keyword-search/stores/shortlist.store';
    import TooMuchSearch from '~/features/keyword-search/TooMuchSearch.vue';

    import {SearchStartedSourceComponents, useSearchTracking} from '~/features/keyword-search/useSearchTracking';

    const {t} = useTranslation();
    const route = useRoute();

    useAmplitudeSessionReplay(() => route.query.sourceComponent === SearchStartedSourceComponents.advanced_search_dispatch);

    const router = useRouter();
    const authStore = useAuth();
    const {loggedIn} = storeToRefs(authStore);

    const filtersStore = useFiltersStore();
    const profilesStore = useProfilesStore();
    const searchFeaturesStore = useSearchFeaturesStore();
    const searchUrlStore = useSearchUrlStore();
    const shortlistStore = useShortlistStore();
    const restrictionsStore = useRestrictionsStore();
    const displayModeStore = useNavbarDisplayModeStore();
    const {setNavbarDisplayMode} = displayModeStore;

    if (loggedIn.value && authStore.isCompanySuspended) {
        safelyNavigateTo('/client-settings/restricted', {externalToThisApp: true});
    }

    searchUrlStore.update(route.query);

    const {isStrategyEligible, translationPreference} = storeToRefs(searchFeaturesStore);
    const {performedTooMuchSearch, shouldRedirectExternal} = storeToRefs(restrictionsStore);
    const {searchUrlParams} = storeToRefs(searchUrlStore);
    const {initialSearchResponse, isLoading: isLoadingResults} = storeToRefs(profilesStore);

    // UseAsyncData handler function parameter MUST return something and it MUST be different of null or undefined.
    // See documentation https://nuxt.com/docs/api/composables/use-async-data#params.
    // Here it's tricky: Promise.all([some, array, of, promises]) returns an array with the result of each promise and this array is never null or undefined.
    await useAsyncData(
        'search-content',
        () =>
            Promise.all([
                authStore.isLogged(),
                fetchProfilesAndFallbacks(),
                filtersStore.fetchFilters(),
                shortlistStore.fetchShortlist(),
                searchFeaturesStore.fetchFeatures(),
            ]),
        {deep: true},
    );

    async function fetchProfilesAndFallbacks() {
        await profilesStore.fetchProfiles();
        await profilesStore.fetchRemoteFallbackProfiles();
        await profilesStore.fetchEuropaFallbackProfiles();
    }

    function onSearchRequestedEvent(payload: NavbarSearchRequestedPayload) {
        searchUrlStore.updateUrlParams(payload.query, payload.location);
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            navbarEventBus.on('navbar-search-requested', onSearchRequestedEvent);
            if (loggedIn.value) {
                setNavbarDisplayMode({mode: 'REDUCED'});
            }
        });

        onBeforeUnmount(() => {
            navbarEventBus.off('navbar-search-requested', onSearchRequestedEvent);
        });
    }

    // anytime the url query params change, we must update filters and results
    watch(searchUrlParams, () => Promise.all([filtersStore.fetchFilters(), fetchProfilesAndFallbacks()]), {deep: true});

    // anytime the user translation preference changes, we must update europa fallback results
    watch(
        translationPreference,
        async () => {
            await profilesStore.fetchEuropaFallbackProfiles();
        },
        {deep: true},
    );

    // anytime the query of the current page change, we must update search features
    watch([() => searchUrlParams.value.query, () => searchUrlParams.value.page], () => searchFeaturesStore.fetchFeatures());

    // anytime the current route query params changes, we update the sanitized url and params state
    watch(
        () => route.query,
        (newRouteQuery) => searchUrlStore.update(newRouteQuery),
    );

    watch(isLoadingResults, async (isLoadingNow, wasLoadingBefore) => {
        // update route location only when the search results are loaded completely
        if (wasLoadingBefore && !isLoadingNow) {
            await router.push({query: searchUrlStore.toRouterQueryParams()});
        }
    });

    watch(
        shouldRedirectExternal,
        async (to) => {
            if (to) {
                try {
                    await safelyNavigateTo(to, {externalToThisApp: true});
                } catch (_) {
                    // On client side, we can have this error this would flood Sentry:
                    // "cannot read properties of undefined (reading 'appMiddleware')"
                    // It comes from nuxt router plugin but is probably linked to a "bad" usage of something on our end.
                }
            }
        },
        {immediate: true},
    );

    useSearchTracking();

    const title = t('search.result.title', [searchUrlParams.value.query]);
    const description = t('search.meta.description', [initialSearchResponse.value?.pagination.totalElements, searchUrlParams.value.query]);

    useServerSeoMeta({
        title,
        description,
        robots: 'noindex, nofollow',
    });

    useTwitterMetaTags(
        new TwitterMetaTags({
            card: 'summary',
            title,
            description,
            t,
        }),
    );

    useOpenGraphMetaTags(
        new OpenGraphMetaTag({
            title,
            description,
            t,
        }),
    );

    definePageMeta({
        middleware: [missingQueryParamRedirect],
    });
</script>
